import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ChallengePasswordForm from './ChallengePasswordForm';
import Brand from './Brand';

const LoginPage = () => {
  const { login, completePassword, forgotPassword, confirmForgotPassword, sessionExpired } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetNotice, setResetNotice] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await login(username, password);
      if (result.new_password_required) {
        setChallenge(result);
        return;
      }
      if (result.mfa_required || result.mfa_setup_required) {
        setError('MFA is not enabled for this beta login yet. Ask an admin to disable MFA on the account.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const finishInvite = async (newPassword) => {
    const challengeUsername = challenge.username || username;
    const result = await completePassword(
      challengeUsername,
      challenge.session,
      newPassword
    );
    if (result.new_password_required) {
      setChallenge(result);
      throw new Error('Cognito still requires a new password.');
    }
    if (result.mfa_required || result.mfa_setup_required) {
      setChallenge(null);
      setError('MFA is not enabled for this beta login yet. Ask an admin to disable MFA on the account.');
    }
  };

  const beginReset = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await forgotPassword(username);
      setResetSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const finishReset = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await confirmForgotPassword(username, resetCode, resetPassword);
      setResetMode(false);
      setResetSent(false);
      setResetCode('');
      setResetPassword('');
      setResetNotice('Password updated. Sign in with your new password.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-shell">
      <section className="login-art" aria-hidden="true">
        <div className="orbit orbit-one" />
        <div className="orbit orbit-two" />
        <p className="eyebrow">Research intelligence</p>
        <h1>See the company beneath the story.</h1>
        <p className="login-art-copy">A living research corpus for investors who want the evidence, the tension, and the unanswered questions in one place.</p>
      </section>
      <section className="login-panel">
        <Brand />
        <p className="eyebrow">Rebellionaire.AI research</p>
        <h2>{challenge ? 'Finish setup.' : resetMode ? 'Reset password.' : 'Welcome back.'}</h2>
        {sessionExpired && <div className="error-box" role="alert">Your session has expired. Please sign in again.</div>}
        <div className={`login-auth-section${!challenge && !resetMode ? ' login-signin-section' : ''}`}>
          <p className="muted">
            {challenge
              ? 'Your invite used a temporary password. Choose a permanent one to continue.'
              : resetMode
                ? resetSent ? 'Enter the code from your email and choose a new password.' : 'We will email a password reset code to your account.'
                : 'Sign in to continue.'}
          </p>
          {challenge ? (
            <ChallengePasswordForm
              username={username}
              onSubmit={finishInvite}
              onCancel={() => { setChallenge(null); setPassword(''); }}
            />
          ) : resetMode ? (
            <form onSubmit={resetSent ? finishReset : beginReset} className="login-form">
              <label htmlFor="reset-username">Email</label>
              <input id="reset-username" type="email" value={username} onChange={(event) => setUsername(event.target.value)} required autoComplete="username" />
              {resetSent && <>
                <label htmlFor="reset-code">Email code</label>
                <input id="reset-code" value={resetCode} onChange={(event) => setResetCode(event.target.value)} required autoComplete="one-time-code" />
                <label htmlFor="reset-password">New password</label>
                <input id="reset-password" type="password" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} required minLength={12} autoComplete="new-password" />
              </>}
              {error && <div className="error-box" role="alert">{error}</div>}
              <button className="primary-button" type="submit" disabled={busy}>{busy ? 'Please wait...' : resetSent ? 'Set new password' : 'Send reset code'}</button>
              {resetSent && <button className="text-action" type="button" onClick={beginReset} disabled={busy}>Send another code</button>}
              <button className="ghost-button login-back-button" type="button" onClick={() => { setResetMode(false); setResetSent(false); setError(''); }}>Back to sign in</button>
            </form>
          ) : (
            <form onSubmit={submit} className="login-form">
              <label htmlFor="username">Email</label>
              <input id="username" type="email" value={username} onChange={(event) => setUsername(event.target.value)} required autoComplete="username" />
              <label htmlFor="password">Password</label>
              <input id="password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} required autoComplete="current-password" />
              {error && <div className="error-box" role="alert">{error}</div>}
              <button className="primary-button" type="submit" disabled={busy}>{busy ? 'Signing in...' : 'Sign in'} <span aria-hidden="true">↗</span></button>
              <button className="text-action login-reset-link" type="button" onClick={() => { setResetMode(true); setResetNotice(''); setError(''); }}>Forgot password?</button>
            </form>
          )}
        </div>
        {challenge && error && <div className="error-box" role="alert">{error}</div>}
        {resetNotice && <div className="success-box" role="status">{resetNotice}</div>}
        <div className="login-entry-options">
          <a className="primary-button" href="/preview">
            <svg className="login-entry-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <circle cx="10.8" cy="10.8" r="6.8" />
              <path d="m16 16 5 5" />
            </svg>
            <span>Explore the product without an account</span>
          </a>
          <div className="login-entry-divider" role="separator" aria-label="or"><span>OR</span></div>
          <a className="secondary-button" href="/plans">
            <svg className="login-entry-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="2.5" y="3" width="6" height="6" rx="1" />
              <path d="m4 6 1.5 1.5L8 4.8M12 6h9M2.5 12h6v6h-6zM12 15h9M2.5 21h6v-1M12 21h9" />
            </svg>
            <span>Choose a plan and create an account</span>
          </a>
        </div>
      </section>
    </main>
  );
};

export default LoginPage;
